import React, { useState, useEffect } from 'react';
import { transactionService } from '../../services/transactionService';
import api from '../../services/api';

const TransactionDetails = ({ transactionId, transactionData, onClose, isOpen = true, canPrintCopy = true }) => {
  const [transaction, setTransaction] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Sprawdź czy to zwrot/korekta
  const isReturn = transactionData?.document_type === 'zwrot' ||
                   transactionData?.typ_transakcji === 'zwrot';

  useEffect(() => {
    if (transactionId && isOpen) {
      loadTransaction();
    }
  }, [transactionId, isOpen]);

  const loadTransaction = async () => {
    setLoading(true);
    setError('');

    try {
      let response;

      if (isReturn) {
        // Dla zwrotów/korekt użyj endpointu zwrotów
        const returnId = transactionData?.zwrot_id || transactionId;
        const apiResponse = await api.get(`/pos/returns/${returnId}`);
        if (apiResponse.data.success) {
          const returnData = apiResponse.data.data;
          // Mapuj dane zwrotu na format oczekiwany przez komponent
          response = {
            success: true,
            data: {
              id: returnData.id,
              numer_paragonu: returnData.numer_zwrotu,
              paragon_zrodlowy: returnData.numer_paragonu,
              data_transakcji: returnData.data_zwrotu,
              czas_transakcji: returnData.czas_zwrotu,
              suma_brutto: returnData.suma_zwrotu_brutto,
              suma_netto: returnData.suma_zwrotu_netto,
              suma_vat: returnData.suma_zwrotu_vat,
              forma_platnosci: returnData.forma_platnosci,
              kasjer_imie: returnData.kasjer_login,
              powod_zwrotu: returnData.powod_zwrotu,
              status: returnData.status,
              items: (returnData.items || []).map(item => ({
                id: item.id,
                nazwa_produktu: item.nazwa_produktu,
                kod_produktu: item.kod_produktu,
                barcode: item.kod_produktu,
                ilosc: item.ilosc_zwracana,
                cena_jednostkowa: item.cena_jednostkowa_brutto,
                wartosc_brutto: item.wartosc_brutto,
                stawka_vat: item.stawka_vat,
                jednostka: 'szt'
              })),
              discounts: [],
              isReturn: true
            }
          };
        } else {
          throw new Error(apiResponse.data.message || 'Błąd pobierania zwrotu');
        }
      } else {
        // Dla normalnych transakcji
        response = await transactionService.getTransaction(transactionId);
      }

      if (response.success) {
        setTransaction(response.data);
      } else {
        setError(response.error || 'Błąd pobierania transakcji');
      }
    } catch (err) {
      setError('Błąd połączenia: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    try {
      return new Date(dateStr).toLocaleDateString('pl-PL', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit'
      });
    } catch {
      return dateStr;
    }
  };

  const formatTime = (timeStr) => {
    if (!timeStr) return '';
    try {
      return timeStr.substring(0, 5); // HH:MM
    } catch {
      return timeStr;
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('pl-PL', {
      style: 'currency',
      currency: 'PLN'
    }).format(amount || 0);
  };

  // Oblicz rabat dla pozycji (proporcjonalnie)
  const getItemDiscount = (item, items, discounts) => {
    if (!discounts || discounts.length === 0) return 0;

    const totalDiscount = discounts.reduce((sum, d) => sum + (parseFloat(d.kwota_rabatu) || 0), 0);
    if (totalDiscount === 0) return 0;

    // Kaucje i produkty wykluczone z rabatu nie mają rabatu
    const vatRate = parseFloat(item.stawka_vat);
    const isDeposit = vatRate === -2 || item.typ_produktu === 'kaucja';
    const isExcluded = item.wykluczony_z_rabatow === 1 || item.wykluczony_z_rabatow === true;
    if (isDeposit || isExcluded) return 0;

    // Suma wartości pozycji które MOGĄ mieć rabat (bez kaucji i wykluczonych)
    const totalValue = items.reduce((sum, i) => {
      const iVatRate = parseFloat(i.stawka_vat);
      const iIsDeposit = iVatRate === -2 || i.typ_produktu === 'kaucja';
      const iIsExcluded = i.wykluczony_z_rabatow === 1 || i.wykluczony_z_rabatow === true;
      if (iIsDeposit || iIsExcluded) return sum;
      return sum + (parseFloat(i.wartosc_brutto || i.cena_calkowita || 0));
    }, 0);

    if (totalValue === 0) return 0;

    const itemValue = parseFloat(item.wartosc_brutto || item.cena_calkowita || 0);
    const itemRatio = itemValue / totalValue;
    return totalDiscount * itemRatio;
  };

  // Nie renderuj jeśli modal jest zamknięty lub brak ID
  if (!isOpen || !transactionId) {
    return null;
  }

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: 'rgba(0,0,0,0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000,
      padding: '1rem'
    }}>
      <div style={{
        background: "var(--card-bg)",
        borderRadius: '6px',
        width: '900px',
        maxWidth: '95vw',
        maxHeight: '90vh',
        overflow: 'hidden',
        boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)'
      }}>
        {/* Header - styl jak w OpenShiftEnhancedModal */}
        <div style={{
          padding: '0.75rem 1rem',
          backgroundColor: 'var(--card-bg)', border: '1px solid var(--border)', color: 'var(--text)' ,
          display: 'flex',
          alignItems: 'center',
          gap: '0.75rem'
        }}>
          <div style={{
            width: '2rem',
            height: '2rem',
            background: 'var(--bg-secondary)',
            borderRadius: '5px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '1rem'
          }}>
            🧾
          </div>
          <div style={{ flex: 1 }}>
            <h5 style={{ margin: 0, fontWeight: '600', fontSize: '14px' }}>
              Szczegóły Paragonu #{transactionId}
            </h5>
            <p style={{ margin: 0, fontSize: '11px', opacity: 0.9 }}>
              {transaction?.numer_paragonu || 'Ładowanie...'}
            </p>
          </div>
          <button
            onClick={onClose}
            style={{
              background: 'none',

              fontSize: '1.25rem',
              color: 'var(--text-secondary)' ,
              cursor: 'pointer',
              padding: '0.25rem',
              lineHeight: 1
            }}
          >
            ✕
          </button>
        </div>

        {/* Body */}
        <div style={{ padding: '1rem', maxHeight: 'calc(90vh - 120px)', overflowY: 'auto' }}>
          {/* Error */}
          {error && (
            <div style={{ 
              backgroundColor: 'var(--danger-light)', 
              color: 'var(--danger)', 
              padding: '10px 12px', 
              borderRadius: '4px', 
              marginBottom: '12px',
              border: '1px solid var(--danger-light)',
              fontSize: '12px'
            }}>
              ❌ {error}
            </div>
          )}

          {/* Loading */}
          {loading && (
            <div style={{ textAlign: 'center', padding: '2rem' }}>
              <div style={{ 
                width: '40px', 
                height: '40px', 
                border: '4px solid var(--border)',
                borderTop: '4px solid var(--info)',
                borderRadius: '50%',
                animation: 'spin 1s linear infinite',
                margin: '0 auto'
              }}></div>
              <p style={{ marginTop: '1rem', color: 'var(--text-muted)', fontSize: '12px' }}>Ładowanie szczegółów...</p>
            </div>
          )}

          {/* Szczegóły transakcji */}
          {!loading && transaction && (
            <div>
              {/* Kafelki z podstawowymi informacjami */}
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(4, 1fr)',
                gap: '10px',
                marginBottom: '16px'
              }}>
                {/* Podstawowe */}
                <div style={{ 
                  padding: '12px',
                  backgroundColor: 'var(--bg)',
                  borderRadius: '4px',
                  border: '1px solid var(--border)'
                }}>
                  <div style={{ fontSize: '10px', color: 'var(--text-muted)', marginBottom: '4px', fontWeight: '500' }}>PODSTAWOWE</div>
                  <div style={{ fontSize: '11px', lineHeight: '1.6' }}>
                    <div><strong>ID:</strong> #{transaction.id}</div>
                    <div><strong>Nr:</strong> {transaction.numer_paragonu || 'N/A'}</div>
                    <div><strong>Data:</strong> {formatDate(transaction.data_transakcji)}</div>
                    <div><strong>Czas:</strong> {formatTime(transaction.czas_transakcji)}</div>
                    <div style={{ marginTop: '4px' }}>
                      <span style={{
                        padding: '2px 6px',
                        borderRadius: '10px',
                        fontSize: '10px',
                        backgroundColor: transaction.status === 'zakonczony' ? 'var(--success-light)' : 'var(--warning-light)',
                        color: transaction.status === 'zakonczony' ? 'var(--success)' : 'var(--warning)',
                        fontWeight: '500'
                      }}>
                        {transaction.status === 'zakonczony' ? '✓ Sfinalizowana' : transaction.status}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Kasjer & Klient */}
                <div style={{ 
                  padding: '12px',
                  backgroundColor: 'var(--bg)',
                  borderRadius: '4px',
                  border: '1px solid var(--border)'
                }}>
                  <div style={{ fontSize: '10px', color: 'var(--text-muted)', marginBottom: '4px', fontWeight: '500' }}>KASJER & KLIENT</div>
                  <div style={{ fontSize: '11px', lineHeight: '1.6' }}>
                    <div><strong>Kasjer:</strong> {transaction.kasjer_login || 'N/A'}</div>
                    <div>
                      <strong>Klient:</strong>{' '}
                      {transaction.nabywca_nazwa || transaction.customer_name || 'Klient detaliczny'}
                    </div>
                    {transaction.nabywca_nip && (
                      <div>
                        <strong>NIP nabywcy:</strong> {transaction.nabywca_nip}
                        {transaction.document_type === 'paragon_nip' && (
                          <span style={{ marginLeft: '6px', fontSize: '10px', color: 'var(--orange)' }}>
                            (paragon z NIP)
                          </span>
                        )}
                      </div>
                    )}
                  </div>
                </div>

                {/* Płatność */}
                <div style={{ 
                  padding: '12px',
                  backgroundColor: 'var(--bg)',
                  borderRadius: '4px',
                  border: '1px solid var(--border)'
                }}>
                  <div style={{ fontSize: '10px', color: 'var(--text-muted)', marginBottom: '4px', fontWeight: '500' }}>PŁATNOŚĆ</div>
                  <div style={{ fontSize: '11px', lineHeight: '1.6' }}>
                    <div>
                      <strong>Metoda:</strong>
                      <span style={{
                        marginLeft: '4px',
                        padding: '2px 6px',
                        borderRadius: '10px',
                        fontSize: '10px',
                        backgroundColor: 
                          transaction.forma_platnosci === 'gotowka' ? 'var(--success-light)' :
                          transaction.forma_platnosci === 'karta' ? 'var(--info-light)' :
                          transaction.forma_platnosci === 'blik' ? 'var(--orange-light)' : 'var(--bg)',
                        color:
                          transaction.forma_platnosci === 'gotowka' ? 'var(--success)' :
                          transaction.forma_platnosci === 'karta' ? 'var(--info)' :
                          transaction.forma_platnosci === 'blik' ? 'var(--orange)' : 'var(--text-secondary)'
                      }}>
                        {transaction.forma_platnosci || 'N/A'}
                      </span>
                    </div>
                    <div><strong>Otrzymano:</strong> {formatCurrency(transaction.kwota_otrzymana)}</div>
                    <div><strong>Reszta:</strong> {formatCurrency(transaction.kwota_reszty)}</div>
                  </div>
                </div>

                {/* Kwoty - wyróżniony */}
                <div style={{
                  padding: '12px',
                  backgroundColor: 'var(--success-light)',
                  borderRadius: '4px',
                  border: '1px solid var(--success-light)'
                }}>
                  <div style={{ fontSize: '10px', color: 'var(--success)', marginBottom: '4px', fontWeight: '500' }}>KWOTY</div>
                  {(() => {
                    const totalDiscount = transaction.discounts?.reduce((sum, d) => sum + (parseFloat(d.kwota_rabatu) || 0), 0) || 0;
                    const sumaBrutto = parseFloat(transaction.suma_brutto) || 0;
                    const sumaPoRabacie = sumaBrutto - totalDiscount;
                    const hasDiscount = totalDiscount > 0;

                    // Oblicz netto/VAT bez kaucji
                    const items = transaction.items || [];
                    let nettoBezkaucji = 0;
                    let vatBezkaucji = 0;
                    let kaucjaRazem = 0;
                    items.forEach(item => {
                      const vat = parseFloat(item.stawka_vat);
                      const brutto = parseFloat(item.wartosc_brutto || item.cena_calkowita || 0);
                      const isDeposit = vat === -2 || item.typ_produktu === 'kaucja';
                      if (isDeposit) {
                        kaucjaRazem += brutto;
                      } else {
                        const vatRate = vat > 0 ? vat : 0;
                        const netto = brutto / (1 + vatRate / 100);
                        nettoBezkaucji += netto;
                        vatBezkaucji += brutto - netto;
                      }
                    });

                    return (
                      <div style={{ fontSize: '11px', lineHeight: '1.6' }}>
                        <div><strong>Netto:</strong> {formatCurrency(nettoBezkaucji)}</div>
                        <div><strong>VAT:</strong> {formatCurrency(vatBezkaucji)}</div>
                        {kaucjaRazem > 0 && <div><strong>Kaucja:</strong> {formatCurrency(kaucjaRazem)}</div>}
                        <div><strong>Brutto:</strong> {formatCurrency(sumaBrutto)}</div>

                        {hasDiscount && (
                          <>
                            <div style={{
                              color: 'var(--danger)',
                              marginTop: '4px',
                              paddingTop: '4px',
                              borderTop: '1px dashed var(--success-light)'
                            }}>
                              <strong>Rabat:</strong> -{formatCurrency(totalDiscount)}
                            </div>
                            <div style={{
                              fontSize: '16px',
                              fontWeight: '700',
                              color: 'var(--success)',
                              marginTop: '6px',
                              paddingTop: '6px',
                              borderTop: '2px solid var(--success)',
                              backgroundColor: 'var(--success-light)',
                              margin: '6px -12px -12px -12px',
                              padding: '8px 12px',
                              borderRadius: '0 0 4px 4px'
                            }}>
                              DO ZAPŁATY: {formatCurrency(sumaPoRabacie)}
                            </div>
                          </>
                        )}

                        {!hasDiscount && (
                          <div style={{
                            fontSize: '14px',
                            fontWeight: '700',
                            color: 'var(--success)',
                            marginTop: '6px',
                            paddingTop: '6px',
                            borderTop: '1px solid var(--success-light)'
                          }}>
                            {formatCurrency(sumaBrutto)}
                          </div>
                        )}
                      </div>
                    );
                  })()}
                </div>
              </div>

              {/* Sekcja Rabatów */}
              {transaction.discounts && transaction.discounts.length > 0 && (
                <div style={{
                  marginTop: '12px',
                  padding: '10px',
                  backgroundColor: 'var(--success-light)',
                  borderRadius: '4px',
                  border: '1px solid var(--success-light)'
                }}>
                  <div style={{
                    fontSize: '11px',
                    fontWeight: '600',
                    color: 'var(--success)',
                    marginBottom: '6px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px'
                  }}>
                    <span>🏷️</span>
                    <span>Zastosowane rabaty</span>
                  </div>
                  {transaction.discounts.map((discount, idx) => (
                    <div key={idx} style={{
                      fontSize: '11px',
                      display: 'flex',
                      justifyContent: 'space-between',
                      padding: '4px 0',
                      borderTop: idx > 0 ? '1px solid var(--success-light)' : 'none'
                    }}>
                      <span>{discount.rabat_nazwa || discount.nazwa || 'Rabat'}</span>
                      <span style={{ fontWeight: '600' }}>
                        -{formatCurrency(discount.kwota_rabatu)}
                      </span>
                    </div>
                  ))}
                </div>
              )}

              {/* Pozycje transakcji */}
              <div style={{ marginTop: '12px' }}>
                <div style={{
                  fontSize: '11px',
                  fontWeight: '600',
                  color: 'var(--text-secondary)',
                  marginBottom: '8px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px'
                }}>
                  <span>📦</span>
                  <span>Pozycje ({transaction.items?.length || 0})</span>
                </div>
                
                {transaction.items && transaction.items.length > 0 ? (
                  <div style={{ 
                    border: '1px solid var(--border)',
                    borderRadius: '4px',
                    overflow: 'hidden'
                  }}>
                    {/* Header tabeli */}
                    <div style={{
                      display: 'grid',
                      gridTemplateColumns: transaction.discounts?.length > 0
                        ? '35px 1fr 120px 50px 75px 70px 40px 65px 80px'
                        : '35px 1fr 120px 50px 75px 70px 40px 80px',
                      gap: '6px',
                      padding: '8px 10px',
                      backgroundColor: 'var(--bg)',
                      borderBottom: '2px solid var(--border)',
                      fontSize: '10px',
                      fontWeight: '600',
                      color: 'var(--text-secondary)'
                    }}>
                      <div>Lp.</div>
                      <div>Produkt</div>
                      <div>Kod</div>
                      <div style={{ textAlign: 'center' }}>Ilość</div>
                      <div style={{ textAlign: 'right' }}>Cena jedn.</div>
                      <div style={{ textAlign: 'right' }}>Netto</div>
                      <div style={{ textAlign: 'center' }}>VAT</div>
                      {transaction.discounts?.length > 0 && (
                        <div style={{ textAlign: 'right', color: 'var(--danger)' }}>Rabat</div>
                      )}
                      <div style={{ textAlign: 'right' }}>Wartość</div>
                    </div>

                    {/* Wiersze */}
                    <div style={{ maxHeight: '250px', overflowY: 'auto' }}>
                      {transaction.items.map((item, index) => {
                        const itemDiscount = getItemDiscount(item, transaction.items, transaction.discounts);
                        const originalValue = parseFloat(item.wartosc_brutto || item.cena_calkowita || item.total_price || 0);
                        const discountedValue = originalValue - itemDiscount;
                        const hasDiscount = itemDiscount > 0;

                        return (
                          <div
                            key={index}
                            style={{
                              display: 'grid',
                              gridTemplateColumns: transaction.discounts?.length > 0
                                ? '35px 1fr 85px 50px 75px 75px 45px 70px 80px'
                                : '35px 1fr 85px 50px 75px 75px 45px 80px',
                              gap: '6px',
                              padding: '8px 10px',
                              borderBottom: index < transaction.items.length - 1 ? '1px solid var(--bg)' : 'none',
                              backgroundColor: index % 2 === 0 ? 'var(--card-bg)' : 'var(--bg)',
                              fontSize: '11px',
                              alignItems: 'center'
                            }}
                          >
                            <div style={{ color: 'var(--text-muted)' }}>{index + 1}</div>
                            <div style={{ fontWeight: '500', color: 'var(--text)' }}>
                              {item.product_name || item.nazwa_produktu || item.nazwa}
                            </div>
                            <div>
                              <code style={{
                                fontSize: '9px',
                                backgroundColor: 'var(--border)',
                                padding: '2px 4px',
                                borderRadius: '3px',
                                whiteSpace: 'nowrap'
                              }}>
                                {item.barcode || item.kod_produktu || '-'}
                              </code>
                            </div>
                            <div style={{ textAlign: 'center' }}>
                              {item.ilosc || item.quantity} {item.jednostka || item.unit || 'szt'}
                            </div>
                            <div style={{ textAlign: 'right' }}>
                              {formatCurrency(item.cena_jednostkowa || item.unit_price)}
                            </div>
                            <div style={{ textAlign: 'right', fontSize: '10px', color: 'var(--text-muted)' }}>
                              {(() => {
                                const vat = parseFloat(item.stawka_vat);
                                const isDeposit = vat === -2 || item.typ_produktu === 'kaucja';
                                if (isDeposit) return '-';
                                const brutto = parseFloat(item.wartosc_brutto || item.cena_calkowita || 0);
                                const vatRate = vat > 0 ? vat : 0;
                                return formatCurrency(brutto / (1 + vatRate / 100));
                              })()}
                            </div>
                            <div style={{ textAlign: 'center', fontSize: '10px' }}>
                              {item.stawka_vat == -2 || item.typ_produktu === 'kaucja' ? 'nd' : `${item.stawka_vat || 23}%`}
                            </div>
                            {transaction.discounts?.length > 0 && (
                              <div style={{ textAlign: 'right', color: hasDiscount ? 'var(--danger)' : 'var(--text-muted)', fontSize: '10px' }}>
                                {hasDiscount ? `-${formatCurrency(itemDiscount)}` : '-'}
                              </div>
                            )}
                            <div style={{ textAlign: 'right', fontWeight: '600', color: 'var(--success)' }}>
                              {hasDiscount ? (
                                <div>
                                  <span style={{ textDecoration: 'line-through', color: 'var(--text-muted)', fontSize: '9px', marginRight: '4px' }}>
                                    {formatCurrency(originalValue)}
                                  </span>
                                  <span>{formatCurrency(discountedValue)}</span>
                                </div>
                              ) : (
                                formatCurrency(originalValue)
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>

                    {/* Footer */}
                    <div style={{
                      padding: '8px 10px',
                      backgroundColor: 'var(--bg)',
                      borderTop: '1px solid var(--border)',
                      fontSize: '10px',
                      color: 'var(--text-muted)',
                      display: 'flex',
                      justifyContent: 'space-between'
                    }}>
                      <span>Pozycji: <strong>{transaction.items.length}</strong></span>
                      <span>Łączna ilość: <strong>
                        {transaction.items.reduce((sum, item) => sum + (item.ilosc || item.quantity || 0), 0)}
                      </strong></span>
                    </div>
                  </div>
                ) : (
                  <div style={{ 
                    textAlign: 'center', 
                    padding: '20px', 
                    color: 'var(--text-muted)',
                    backgroundColor: 'var(--bg)',
                    borderRadius: '4px',
                    fontSize: '12px'
                  }}>
                    📭 Brak pozycji w transakcji
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Footer z akcjami */}
        <div style={{
          padding: '12px 16px',
          borderTop: '1px solid var(--border)',
          backgroundColor: 'var(--bg)',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <button
            onClick={loadTransaction}
            style={{
              padding: '6px 12px',
              fontSize: '12px',
              border: '1px solid var(--text-muted)', color: 'var(--text-muted)' ,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '6px'
            }}
          >
            🔄 Odśwież
          </button>
          
          <div style={{ display: 'flex', gap: '8px' }}>
            {canPrintCopy && (
              <button
                onClick={() => {
                  // Dla zwrotów/korekt użyj strony korekty
                  const isCorrection = transaction?.typ_transakcji === 'zwrot' || transaction?.document_type === 'zwrot';
                  if (isCorrection) {
                    window.open(`/pos/correction-print/${transaction?.zwrot_id || transactionId}`, '_blank');
                  } else {
                    window.open(`/pos/receipt/${transactionId}`, '_blank');
                  }
                }}
                style={{
                  padding: '6px 12px',
                  fontSize: '12px',
                  backgroundColor: transaction?.typ_transakcji === 'zwrot' || transaction?.document_type === 'zwrot' ? 'var(--danger)' : 'var(--success)',
                  color: '#fff' ,
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px'
                }}
              >
                {transaction?.typ_transakcji === 'zwrot' || transaction?.document_type === 'zwrot'
                  ? '🖨️ Drukuj korektę paragonu'
                  : '🖨️ Drukuj paragon'}
              </button>
            )}
            
            <button
              onClick={onClose}
              style={{
                padding: '6px 12px',
                fontSize: '12px',
                border: '1px solid var(--info)', color: 'var(--info)' ,
                cursor: 'pointer'
              }}
            >
              Zamknij
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default React.memo(TransactionDetails);
